import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, post, del, put } from "../../Services/ApiRequest";
import apiRequest from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  downloadBlob,
  exportDownloadErrorMessage,
  filenameFromContentDisposition,
} from "../../Utils/Helper/downloadFile";
import { toast } from "../../Utils/Helper/toast";

// Types for the API payload - Customer object in bulk request
export interface CustomerPayload {
  name: string;
  email: string;
  phone: string;
  contact_person: string;
  churn_risk: string;
  city: string;
  country: string;
  employees?: number;
  revenue?: number;
  fiscal_year_end?: string;
  tier: string;
  industry?: string;
}

// Bulk create accounts payload
export interface BulkCreateAccountsPayload {
  batch_name: string | null;
  approver_id: string;
  customers: CustomerPayload[];
  upload_type?: "customer_update" | "add_customer";
}

// Bulk delete accounts payload
export interface BulkDeleteAccountsPayload {
  batch_name: string;
  approver_id: string;
  customers: Array<{
    name: string;
    customer_id: string;
  }>;
  upload_type: "delete_customer";
}

// Approval Request Types
export interface ApprovalRequestCustomer {
  name: string;
  email: string;
  phone: string;
  revenue: number | null;
  renewal_date: string | null;
  nrr_score: number;
  company_size: string | null;
  industry_vertical: string | null;
  business_model: string | null;
  public_status: string | null;
  founded_year: number | null;
  headquarters_location: string | null;
  annual_revenue: number | null;
  revenue_currency: string;
  fiscal_year_end: string | null;
  customer_since: string | null;
  acquisition_source: string | null;
  initial_product: string | null;
  timezone: string | null;
  primary_language: string | null;
  secondary_languages: string | null;
  segment: string | null;
  tier: string;
  account_status: string;
  churn_risk: string;
  city: string;
  country: string;
  contact_person: string;
  employees: number | null;
  customer_id: string;
  is_active: boolean;
  always_show_on_channel: boolean;
  created_at: string;
  updated_at: string;
  last_enriched_at: string | null;
}

export interface ExecutiveProfile {
  profile_id: string;
  user_id: string;
  city: string | null;
  country: string | null;
  region: string;
  latitude: number | null;
  longitude: number | null;
  specialization: string | null;
  experience_level: string | null;
  account_limit: number | null;
  max_accounts: number | null;
  current_accounts: number | null;
  serving: number | null;
  available_capacity: number | null;
  specialties: Record<string, any> | null;
  languages: Record<string, any> | null;
  timezone: string | null;
  join_date: string | null;
  performance_score: number | null;
  utilization: number | null;
  grade: number;
  availability_status: string | null;
  profile_picture_url: string | null;
  is_active: boolean;
  role: string;
}

export interface ApprovalRequestUser {
  user_id: string;
  email: string;
  full_name: string;
  phone: string;
  position: string;
  image_url: string | null;
  status: string;
  preferences: Record<string, any>;
  last_login_at: string | null;
  last_login_ip: string | null;
  role_id: string;
  created_at: string;
  updated_at: string;
  executiveProfile: ExecutiveProfile;
}

export interface ApprovalRequestItem {
  batch_id: string;
  batch_name: string;
  customer_count: number;
  user_count: number;
  uploaded_at: string;
  uploaded_by_name: string;
  status: "pending" | "approved" | "rejected" | "activated";
  upload_type:
    | "customers"
    | "users"
    | "composite"
    | "delete_customer"
    | "add_customer"
    | "add_user"
    | "upload_account_team_members"
    | "upload_customer_team_members"
    | "add_account_team_members"
    | "add_customer_team_members"
    | "update_account_team_members"
    | "update_customer_team_members";
  record_count: number;
  customers: ApprovalRequestCustomer[];
  users: ApprovalRequestUser[];
  composite?: any[];
  updated_by?: string | null;
  updated_at?: string | null;
  approver_id?: string | null;
  approver_name?: string | null;
}

export interface ApprovalRequestsCounts {
  total_active_executives: number;
  total_active_customers: number;
  total_assignments: number;
}

export interface ApprovalRequestsResponse {
  total: number;
  items: ApprovalRequestItem[];
  counts: ApprovalRequestsCounts;
}

// Approve/Reject request payload
export interface ApproveRejectPayload {
  batch_id: string;
  status: "approved" | "rejected";
}

// Account with sponsors API response types
export interface ActiveCustomerWithSponsorsItem {
  longitude: number | null;
  latitude: number | null;
  customer_id: string;
  name: string;
  region: string | null;
  industry: string | null;
  location: string | null;
  industry_location: string | null;
  primary_sponsors: string | null;
  primary_sponsors_id: string | null;
  secondary_sponsors: string | null;
  secondary_sponsors_id: string | null;
  tiers: string;
  last_level_date: string | null;
  alignment_level: string | null;
  revenue: number;
  renewal: string | null;
  level: string | null;
  email: string;
  phone: string;
  contact_person: string;
  churn_risk: string | null;
  city: string | null;
  country: string | null;
  employees: number | null;
  is_active: boolean;
  fiscal_year_end: string | null;
}

export interface ActiveCustomersWithSponsorsResponse {
  total: number;
  items: ActiveCustomerWithSponsorsItem[];
}

// Upload Stats Types
export interface RecentBatch {
  id: string;
  batchName: string;
  uploadType: "customers" | "users";
  status: "pending" | "approved" | "rejected" | "activated";
  recordCount: number;
  uploadedAt: string;
}

export interface UploadStatsResponse {
  pendingCount: number;
  approvedCount: number;
  recentBatches: RecentBatch[];
}

// Activation Ready Types
export interface ActivationReadyAccount {
  customer_id: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
}

export interface ActivationReadyExecutive {
  user_id: string;
  email: string;
  full_name: string;
  position: string;
}

export interface ActivationReadyRelationship {
  id: string | null;
  accountId: string;
  customer_id: string;
  executiveId: string;
  primary_executive_id: string;
  status: string;
  assigned_date: string | null;
  due_date: string | null;
  completion_date: string | null;
  account: ActivationReadyAccount;
  primary_executive: ActivationReadyExecutive;
  secondaryExecutive: ActivationReadyExecutive | null;
  submitted_by: string | null;
  submitted_by_name: string | null;
  assigned_by: string | null;
  assigned_by_name: string | null;
  batch_name: string | null;
  batch_id: string | null;
  prep_activated_at: string | null;
  relationship_type: string | null;
  assignment_priority: string | null;
  assessment_id: string | null;
  assessment_title: string | null;
  assessment_type: string | null;
  type: string | null;
  created_at: string;
  updated_at: string;
  approval_status: string;
  notes: string | null;
  ib_result?: IBResultsPayload | null;
}

export interface PrepActivatedBatch {
  batch_id: string;
  batch_name: string;
  upload_type: string;
  record_count: number;
  prep_activated_at: string;
  prep_activated_by: string;
  prep_activated_by_name: string | null;
  uploaded_at: string;
  uploaded_by_name: string;
  relationships: ActivationReadyRelationship[];
}

/** Account shape as returned in approved_assignments from fetchActivationReady */
export interface ApprovedAssignmentAccount {
  customer_id: string | null;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
  tier: string;
  city: string;
  country: string;
  industry: string | null;
  revenue: number;
}

/** Executive shape as returned in approved_assignments from fetchActivationReady */
export interface ApprovedAssignmentExecutive {
  user_id: string | null;
  email: string;
  full_name: string;
  position: string;
  type: "primary" | "secondary";
}

export interface IBResultsPayload {
  is_ibuser_created?: boolean;
  is_ibaccount_created?: boolean;
  is_ibbriefing_generated?: boolean;
  accounts_created?: any[];
  users_created?: any[];
  briefings_generated?: any[];
  errors?: any[];
}

/** Single approved assignment row from fetchActivationReady (one account + one executive) */
export interface ApprovedAssignment {
  id: string;
  customer_id: string;
  executive_id: string;
  status: string;
  assigned_date: string | null;
  assignment_source: string;
  due_date: string | null;
  completion_date: string | null;
  account: ApprovedAssignmentAccount;
  executive: ApprovedAssignmentExecutive;
  submitted_by: string;
  submitted_by_name: string;
  assigned_by: string | null;
  assigned_by_name: string | null;
  batch_name: string | null;
  created_at: string;
  updated_at: string;
  approval_status: string;
  notes: string | null;
  ib_result: IBResultsPayload | null;
}

export interface ActivationReadyResponse {
  approved_assignments: ApprovedAssignment[];
  prep_activated_batches: PrepActivatedBatch[];
  total_approved_assignments: number;
  total_prep_activated_batches: number;
  ongoing: number;
  complete: number;
  incomplete: number;
  total: number;
}

// Assignments Overview Types
export interface AssignmentsOverviewExecutive {
  user_id: any;
  name: string;
  email: string;
  phone: string;
  title: string;
  region: string | null;
  specialization: string | null;
  experience_level: string | null;
  max_accounts: number | null;
  certifications: string[] | null;
  languages: string[] | null;
  timezone: string | null;
  type?: string | null;
  repso_type?: string | null;
}

export interface AssignmentsOverviewAccount {
  customer_id: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
  tier: string;
}

export interface AssignmentsOverviewBatch {
  batch_name: string;
  upload_type: string;
  status: string;
  record_count: number;
  uploaded_by: string;
  uploaded_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  prep_activated_by: string | null;
  prep_activated_at: string | null;
  activated_by: string | null;
  activated_at: string | null;
}

export interface AssignmentsOverviewLoginStatus {
  login_status: string;
  last_login: string;
  user_account_exists: string;
  needs_temp_password: boolean;
}

export interface AssignmentsOverviewAssignmentRequest {
  assigned_at: string;
  updated_at: string;
  assigned_by: string;
  batch_name: string;
  approved_at: string;
  approved_by: string;
}

export interface AssignmentsOverviewItem {
  id: string;
  type: "batch" | "assignment_request";
  executive: AssignmentsOverviewExecutive;
  account: AssignmentsOverviewAccount;
  batch: AssignmentsOverviewBatch | null;
  assignment_request_id: string | null;
  assignment_request_status: string | null;
  assignment_request?: AssignmentsOverviewAssignmentRequest | null;
  assignment_source?: string;
  activated_date: string;
  login_status: AssignmentsOverviewLoginStatus;
}

export interface AssignmentsOverviewResponse {
  items: AssignmentsOverviewItem[];
  total: number;
}

// Fetch accounts filters payload
export interface FetchAccountsFilters {
  assigned_status?: "assigned" | "unassigned" | null;
  tier?: string | null;
  region?: string | null;
  level?: string | null;
}

// Watch List Types
export interface WatchListCustomer {
  Industry: string;
  Location: string;
  "Primary Sponsors": string;
  "Secondary Sponsors": string;
  Revenue: string;
  Renewal: string;
  name: string;
  tier: string;
  customer_id?: string;
  assigned_at?: string;
}

export interface WatchListItem {
  watch_list_id: string;
  watch_list_name: string;
  user_id: string;
  customer_count: number;
  created_at: string;
  updated_at: string;
  customers: WatchListCustomer[];
}

export type WatchListsResponse = WatchListItem[];

// Create watch list payload
export interface CreateWatchListPayload {
  watch_list_name: string;
  customer_ids: string[];
}

// Executive Sponsors Types
export interface ExecutiveAccount {
  name: string;
  tier: string;
  assigned_on: string;
  /** Relationship type between this executive and the account (e.g. primary / secondary) */
  assignment_type?: "primary" | "secondary" | string;
  arr: number;
  nrr: number;
  csat: number | null;
  nps: number | null;
}

export interface Executive {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  city: string | null;
  country: string | null;
  region: string;
  latitude: number | null;
  longitude: number | null;
  grade: number;
  gradeLabel: string;
  experienceLevel: string | null;
  accountLimit: number | null;
  current_accounts: number;
  total_account_count: number;
  performanceScore?: number | null;
  utilization: number;
  role: string;
  specialization: string | null;
  specialties: string[];
  languages: string[];
  certifications: string[];
  joinDate: string | null;
  isActive: boolean;
  availabilityStatus: string | null;
  createdAt: string;
  updatedAt: string;
  primaryAccounts: Array<{ name: string }>;
  accounts: ExecutiveAccount[];
}

// Executive Sponsors Filters
export interface ExecutiveSponsorsFilters {
  assigned_status?: "assigned" | "unassigned" | null;
  /** Optional: include executive leads in the response (default: true) */
  include_exec_leads?: boolean;
}

export interface ExecutiveSponsorsResponse {
  statistics: {
    total_executive_sponsors: number;
    executive_sponsors_assigned_to_accounts: number;
    executive_sponsors_not_assigned_to_accounts: number;
    percentage_assigned_to_accounts: number;
  };
  executives: Executive[];
}

// Bulk Create Executive Sponsors Types
export interface ExecutiveProfilePayload {
  city?: string | null;
  country?: string | null;
  region?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  specialization?: string | null;
  experience_level?: string | null;
  account_limit?: number | null;
  max_accounts?: number | null;
  current_accounts?: number;
  serving?: number;
  available_capacity?: number | null;
  specialties?: {
    domains?: string[];
  } | null;
  languages?: string[] | null;
  certifications?: string[] | null;
  join_date?: string | null;
  performance_score?: number | null;
  utilization?: number | null;
  grade?: number | null;
  availability_status?: string;
  profile_picture_url?: string | null;
  is_active?: boolean;
}

export interface UserPayload {
  email: string;
  full_name: string;
  phone: string | null;
  position: string | null;
  image_url?: string | null;
  status?: string | null;
  preferences?: Record<string, any> | null;
  last_login_at?: string | null;
  last_login_ip?: string | null;
  role_id: string | null;
  executiveProfile: ExecutiveProfilePayload;
}

export interface BulkCreateUsersPayload {
  batch_name: string | null;
  approver_id: string;
  users: UserPayload[];
  upload_type?: "user_update" | "add_user";
}

// Bulk Upload Types
export interface BulkUploadParams {
  file: File;
  type:
    | "executives"
    | "accounts"
    | "composite"
    | "upload_account_team_members"
    | "upload_customer_team_members";
  approverId: string;
  /** `null` = omit `batch_name` so the backend receives `None` (multipart has no JSON null). */
  batchName: string | null;
}

// Command Center Types
export interface CommandCenterMetricsResponse {
  totalAccounts: number;
  accountsWithSponsors: number;
  accountsWithoutSponsors: number;
  totalSponsors: number;
  sponsorsOnBench: number;
  pendingSponsorRequest: number;
  avgSponsorCSAT: number;
}

export interface AccountsNeedSponsorsItem {
  id: string;
  account: string;
  category: string;
  level: string;
  raisedAt: string;
  action: string;
}

export interface SponsorsOnBenchItem {
  id: string;
  sponsor: string;
  csat: string | null;
  retention: string | null;
  region?: string | null;
  experienceLevel?: string | null;
  utilization?: number;
  capacity: string;
  playbooks: string | null;
  availableCapacity: number;
}

export interface AccountSurveyItem {
  id: string;
  accountId: string;
  account: string;
  assessmentId?: string;
  sentTo: number;
  progress?: number;
  total?: number;
  sentAt: string;
  due: string;
  survey: string;
  assessmentLevel?: string;
  status: string;
}

export interface AccountTeamCsvValidateFieldError {
  row: number;
  field?: string | null;
  message: string;
}

export interface AccountTeamCsvValidateRoleMismatch {
  row: number;
  email: string;
  account_id: string;
  incoming_role?: string | null;
  current_role: string;
}

export interface AccountTeamCsvValidateMembershipIssue {
  row: number;
  email: string;
  account_id: string;
  message: string;
}

export interface AccountTeamCsvValidateDuplicateRows {
  account_id: string;
  email: string;
  rows: number[];
}

export interface AccountTeamCsvValidateIntraBatchRoleConflict {
  email: string;
  roles: string[];
  rows: number[];
}

export interface AccountTeamCsvValidateInvalidRole {
  row: number;
  role: string;
  message: string;
}

export interface AccountTeamCsvValidateRoleLimitViolation {
  role_key: string;
  limit: number;
  existing: number;
  incoming: number;
  projected: number;
}

export interface AccountTeamCsvValidateRoleLimitAccount {
  account_name?: string | null;
  violations?: AccountTeamCsvValidateRoleLimitViolation[];
}

export interface AccountTeamCsvValidateRoleLimits {
  role_limits?: Record<string, number>;
  by_account_id?: Record<string, AccountTeamCsvValidateRoleLimitAccount>;
}

export interface AccountTeamCsvValidateResult {
  valid: boolean;
  total_rows: number;
  parse_errors?: string[];
  validation_errors?: AccountTeamCsvValidateFieldError[];
  role_mismatches?: AccountTeamCsvValidateRoleMismatch[];
  duplicate_memberships?: AccountTeamCsvValidateMembershipIssue[];
  duplicate_rows?: AccountTeamCsvValidateDuplicateRows[];
  intra_batch_role_conflicts?: AccountTeamCsvValidateIntraBatchRoleConflict[];
  missing_name_for_new_users?: AccountTeamCsvValidateMembershipIssue[];
  invalid_roles?: AccountTeamCsvValidateInvalidRole[];
  warnings?: AccountTeamCsvValidateFieldError[];
  role_limits?: AccountTeamCsvValidateRoleLimits;
}

interface OpsState {
  accounts: {
    loading: boolean;
    error: string | null;
    success: boolean;
    data: ActiveCustomersWithSponsorsResponse | null;
  };
  approvalRequests: {
    data: ApprovalRequestsResponse | null;
    loading: boolean;
    error: string | null;
  };
  approveReject: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  uploadStats: {
    data: UploadStatsResponse | null;
    loading: boolean;
    error: string | null;
  };
  watchLists: {
    data: WatchListsResponse | null;
    loading: boolean;
    error: string | null;
  };
  createWatchList: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  updateWatchList: {
    loading: boolean;
    error: string | null;
  };
  deleteWatchList: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  executiveSponsors: {
    data: ExecutiveSponsorsResponse | null;
    loading: boolean;
    error: string | null;
  };
  createUsersBulk: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  bulkUpload: {
    isUploading: boolean;
    error: string | null;
    success: boolean;
  };
  deleteAccountsBulk: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  activationReady: {
    data: ActivationReadyResponse | null;
    loading: boolean;
    error: string | null;
  };
  assignmentsOverview: {
    data: AssignmentsOverviewResponse | null;
    loading: boolean;
    error: string | null;
  };
  bulkActivationAction: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  sendPrepActivationEmail: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  commandCenterMetrics: {
    data: CommandCenterMetricsResponse | null;
    loading: boolean;
    error: string | null;
  };
  commandCenterAccountsNeedSponsors: {
    data: AccountsNeedSponsorsItem[] | null;
    loading: boolean;
    error: string | null;
  };
  commandCenterSponsorsOnBench: {
    data: SponsorsOnBenchItem[] | null;
    loading: boolean;
    error: string | null;
  };
  commandCenterAccountSurveys: {
    data: AccountSurveyItem[] | null;
    loading: boolean;
    error: string | null;
  };
  accountTeamCsvValidate: {
    data: AccountTeamCsvValidateResult | null;
    loading: boolean;
    error: string | null;
  };
  accountsExport: {
    loading: boolean;
    error: string | null;
  };
  // Add more ops-related state here as needed
}

const initialState: OpsState = {
  accounts: {
    loading: false,
    error: null,
    success: false,
    data: null,
  },
  approvalRequests: {
    data: null,
    loading: false,
    error: null,
  },
  approveReject: {
    loading: false,
    error: null,
    success: false,
  },
  uploadStats: {
    data: null,
    loading: false,
    error: null,
  },
  watchLists: {
    data: null,
    loading: false,
    error: null,
  },
  createWatchList: {
    loading: false,
    error: null,
    success: false,
  },
  updateWatchList: {
    loading: false,
    error: null,
  },
  deleteWatchList: {
    loading: false,
    error: null,
    success: false,
  },
  executiveSponsors: {
    data: null,
    loading: false,
    error: null,
  },
  createUsersBulk: {
    loading: false,
    error: null,
    success: false,
  },
  bulkUpload: {
    isUploading: false,
    error: null,
    success: false,
  },
  deleteAccountsBulk: {
    loading: false,
    error: null,
    success: false,
  },
  activationReady: {
    data: null,
    loading: false,
    error: null,
  },
  assignmentsOverview: {
    data: null,
    loading: false,
    error: null,
  },
  bulkActivationAction: {
    loading: false,
    error: null,
    success: false,
  },
  sendPrepActivationEmail: {
    loading: false,
    error: null,
    success: false,
  },
  commandCenterMetrics: {
    data: null,
    loading: false,
    error: null,
  },
  commandCenterAccountsNeedSponsors: {
    data: null,
    loading: false,
    error: null,
  },
  commandCenterSponsorsOnBench: {
    data: null,
    loading: false,
    error: null,
  },
  commandCenterAccountSurveys: {
    data: null,
    loading: false,
    error: null,
  },
  accountTeamCsvValidate: {
    data: null,
    loading: false,
    error: null,
  },
  accountsExport: {
    loading: false,
    error: null,
  },
};

export type AccountsExportScope = "all" | "executive_sponsors";
export type AccountsExportFormat = "csv" | "excel";

export const exportAccountsData = createAsyncThunk(
  "ops/accountsExport/download",
  async (
    {
      format,
      scope,
    }: { format: AccountsExportFormat; scope: AccountsExportScope },
    { rejectWithValue }
  ) => {
    const isExcel = format === "excel";
    const url = API_URLS.exportStakeholders(format, scope);
    const scopePrefix =
      scope === "executive_sponsors" ? "executive_sponsors" : "stakeholder";
    const fallbackFilename = `${scopePrefix}_export_${new Date().toISOString().slice(0, 10)}.${
      isExcel ? "xlsx" : "csv"
    }`;
    const fallbackError = isExcel
      ? "Failed to export accounts Excel"
      : "Failed to export accounts CSV";

    try {
      const response = await get(url, {
        responseType: "blob",
        returnFullResponse: true,
      });
      const blob = response.data as Blob;
      const filename = filenameFromContentDisposition(
        response.headers?.["content-disposition"] as string | undefined,
        fallbackFilename
      );
      downloadBlob(blob, filename);
      return filename;
    } catch (error: any) {
      const errorMessage = await exportDownloadErrorMessage(
        error,
        fallbackError
      );
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching accounts with filters
export const fetchAccountsWithSponsors = createAsyncThunk(
  "ops/accounts/fetchWithSponsors",
  async (filters: FetchAccountsFilters = {}, { rejectWithValue }) => {
    try {
      // Build query params, only include non-empty values
      const params: Record<string, string> = {};
      if (filters.assigned_status) {
        params.assigned_status = filters.assigned_status;
      }
      if (filters.tier) {
        params.tier = filters.tier;
      }
      if (filters.region) {
        params.region = filters.region;
      }
      if (filters.level) {
        params.level = filters.level;
      }

      const response = await get(API_URLS.activeCustomersWithSponsors(params));
      return response as ActiveCustomersWithSponsorsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for creating multiple accounts (bulk)
export const createAccountsBulk = createAsyncThunk(
  "ops/accounts/createBulk",
  async (payload: BulkCreateAccountsPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createAccountsBulk(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for deleting multiple accounts (bulk)
export const deleteAccountsBulk = createAsyncThunk(
  "ops/accounts/deleteBulk",
  async (payload: BulkDeleteAccountsPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createAccountsBulk(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching approval requests
export const fetchApprovalRequests = createAsyncThunk(
  "ops/approvalRequests/fetch",
  async (uploadType: string | undefined = undefined, { rejectWithValue }) => {
    try {
      const params = uploadType ? { upload_type: uploadType } : undefined;
      const response = await get(API_URLS.getApprovalRequests(params));
      return response as ApprovalRequestsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch approval requests";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for approving or rejecting requests
export const approveOrRejectRequest = createAsyncThunk(
  "ops/approvalRequests/approveReject",
  async (payload: ApproveRejectPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.approveOrRejectRequest(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve/reject request";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching upload stats
export const fetchUploadStats = createAsyncThunk(
  "ops/uploadStats/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getUploadStats());
      return response as UploadStatsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch upload stats";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching watch lists
export const fetchWatchLists = createAsyncThunk<
  WatchListsResponse,
  { filters?: FetchAccountsFilters } | undefined,
  { rejectValue: string }
>("ops/watchLists/fetch", async (params, { rejectWithValue }) => {
  try {
    const filters = params?.filters;
    // Build query params, only include non-null and non-empty values
    const queryParams: Record<string, string> = {};
    if (filters?.assigned_status) {
      queryParams.assigned_status = filters.assigned_status;
    }
    if (filters?.tier) {
      queryParams.tier = filters.tier;
    }
    if (filters?.region) {
      queryParams.region = filters.region;
    }
    if (filters?.level) {
      queryParams.level = filters.level;
    }

    const response = await get(API_URLS.customerWatchLists(queryParams));
    return response as WatchListsResponse;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch watch lists";
    return rejectWithValue(errorMessage);
  }
});

// Async thunk for creating watch list
export const createWatchList = createAsyncThunk(
  "ops/watchLists/create",
  async (payload: CreateWatchListPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createWatchList(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create watch list";
      return rejectWithValue(errorMessage);
    }
  }
);

export interface UpdateWatchListPayload extends CreateWatchListPayload {
  watch_list_id: string;
}

// Async thunk for updating watch list
export const updateWatchList = createAsyncThunk(
  "ops/watchLists/update",
  async (payload: UpdateWatchListPayload, { rejectWithValue }) => {
    try {
      const { watch_list_id, ...body } = payload;
      const response = await put(API_URLS.updateWatchList(watch_list_id), body);
      return { watchListId: watch_list_id, response };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update watch list";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for deleting watch list
export const deleteWatchList = createAsyncThunk(
  "ops/watchLists/delete",
  async (watchListId: string, { rejectWithValue }) => {
    try {
      const response = await del(API_URLS.deleteWatchList(watchListId));
      return { watchListId, response };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete watch list";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching executive sponsors
export const fetchExecutiveSponsors = createAsyncThunk(
  "ops/executiveSponsors/fetch",
  async (filters: ExecutiveSponsorsFilters = {}, { rejectWithValue }) => {
    try {
      // Build query params, only include non-null and non-empty values
      const params: Record<string, string> = {};
      if (filters.assigned_status) {
        params.assigned_status = filters.assigned_status;
      }
      // Optional: include executive leads (default true when not specified)
      if (filters.include_exec_leads) {
        params.include_exec_leads = "true";
      }

      const response = await get(
        API_URLS.executiveSponsorsWithAccounts(params)
      );
      return response as ExecutiveSponsorsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch executive sponsors";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for creating users in bulk
export const createUsersBulk = createAsyncThunk(
  "ops/users/createBulk",
  async (payload: BulkCreateUsersPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createUsersBulk(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create users";
      return rejectWithValue(errorMessage);
    }
  }
);

const TEAM_BULK_UPLOAD_TYPES = new Set([
  "upload_account_team_members",
  "upload_customer_team_members",
]);

// Async thunk for bulk upload
export const bulkUpload = createAsyncThunk(
  "ops/bulkUpload",
  async (params: BulkUploadParams, { rejectWithValue }) => {
    try {
      const isTeamBulkUpload = TEAM_BULK_UPLOAD_TYPES.has(params.type);

      const formData = new FormData();
      formData.append("type", params.type);
      formData.append("approver_id", params.approverId);
      formData.append("file", params.file);
      if (!isTeamBulkUpload && params.batchName != null) {
        formData.append("batch_name", params.batchName);
      }

      const response = await apiRequest(
        "POST",
        API_URLS.bulkUpload(),
        formData as any,
        false,
        true
      );

      if (response?.content?.valid === false) {
        return rejectWithValue(
          response?.response?.message || "Something went wrong"
        );
      }

      return response;
    } catch (error: any) {
      try {
        const parsedError = JSON.parse(error?.message);
        return rejectWithValue(parsedError?.message || "Something went wrong");
      } catch {
        return rejectWithValue("Something went wrong");
      }
    }
  }
);

export const validateAccountTeamCsv = createAsyncThunk(
  "ops/accountTeamCsvValidate/validate",
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiRequest(
        "POST",
        API_URLS.validateAccountTeamCsv(),
        formData as any,
        false,
        true
      );

      return (response as { content?: AccountTeamCsvValidateResult })?.content;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to validate account team CSV";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching activation-ready data
export const fetchActivationReady = createAsyncThunk(
  "ops/activationReady/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getActivationReady());
      return response as ActivationReadyResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch activation-ready data";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching assignments overview
export const fetchAssignmentsOverview = createAsyncThunk(
  "ops/assignmentsOverview/fetch",
  async (loginStatus: string | undefined, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getAssignmentsOverview(loginStatus));
      return response as AssignmentsOverviewResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch assignments overview";
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk Activation Action Types
export interface BulkActivationItem {
  customer_id: string;
  executive_id: string;
  assignment_type: string;
}

export interface BulkActivationPayload {
  action: "activate" | "reject";
  items: BulkActivationItem[];
  /** Whether activation emails should be sent (defaults to true on the backend) */
  is_email?: boolean;
}

// Async thunk for bulk activation action
export const bulkActivationAction = createAsyncThunk(
  "ops/bulkActivationAction",
  async (payload: BulkActivationPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.bulkActivationAction(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to perform bulk activation action";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for sending prep activation email
export const sendPrepActivationEmail = createAsyncThunk(
  "ops/sendPrepActivationEmail",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.sendPrepActivationEmail(userId));
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to send prep activation email";

      // Try to parse the error message if it's a stringified JSON
      try {
        if (error?.message) {
          const parsedError = JSON.parse(error.message);
          errorMessage = parsedError.message || errorMessage;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } catch {
        // If parsing fails, try to extract message from error object
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Generic helper function to create command center async thunks
const createCommandCenterThunk = <T>(
  name: string,
  apiUrlFn: (params?: Record<string, any>) => string,
  errorMessage: string,
  isArray = false,
  defaultValue?: T
) => {
  return createAsyncThunk<T, Record<string, any>>(
    `ops/${name}/fetch`,
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
      try {
        const response = await get(apiUrlFn(params));
        // Handle both direct response and wrapped response
        const data = response?.payload || response;

        if (isArray) {
          return (Array.isArray(data) ? data : defaultValue || []) as T;
        }

        return (data || defaultValue) as T;
      } catch (error: any) {
        const errorMsg =
          error?.response?.data?.message || error?.message || errorMessage;
        return rejectWithValue(errorMsg);
      }
    }
  );
};

// Command Center async thunks
export const fetchCommandCenterMetrics =
  createCommandCenterThunk<CommandCenterMetricsResponse>(
    "commandCenterMetrics",
    API_URLS.commandCenterMetrics,
    "Failed to fetch command center metrics",
    false
  );

export const fetchCommandCenterAccountsNeedSponsors = createCommandCenterThunk<
  AccountsNeedSponsorsItem[]
>(
  "commandCenterAccountsNeedSponsors",
  API_URLS.commandCenterAccountsNeedSponsors,
  "Failed to fetch accounts need sponsors",
  true,
  [] as AccountsNeedSponsorsItem[]
);

export const fetchCommandCenterSponsorsOnBench = createCommandCenterThunk<
  SponsorsOnBenchItem[]
>(
  "commandCenterSponsorsOnBench",
  API_URLS.commandCenterSponsorsOnBench,
  "Failed to fetch sponsors on bench",
  true,
  [] as SponsorsOnBenchItem[]
);

export const fetchCommandCenterAccountSurveys = createCommandCenterThunk<
  AccountSurveyItem[]
>(
  "commandCenterAccountSurveys",
  API_URLS.commandCenterAccountSurveys,
  "Failed to fetch account surveys",
  true,
  [] as AccountSurveyItem[]
);

const OpsSlice = createSlice({
  name: "ops",
  initialState,
  reducers: {
    clearAccountsError: state => {
      state.accounts.error = null;
    },
    clearAccountsSuccess: state => {
      state.accounts.success = false;
    },
    resetAccountsState: state => {
      state.accounts.loading = false;
      state.accounts.error = null;
      state.accounts.success = false;
    },
    clearApprovalRequestsError: state => {
      state.approvalRequests.error = null;
    },
    clearApproveRejectError: state => {
      state.approveReject.error = null;
    },
    clearApproveRejectSuccess: state => {
      state.approveReject.success = false;
    },
    clearUploadStatsError: state => {
      state.uploadStats.error = null;
    },
    clearWatchListsError: state => {
      state.watchLists.error = null;
    },
    clearCreateWatchListError: state => {
      state.createWatchList.error = null;
    },
    clearCreateWatchListSuccess: state => {
      state.createWatchList.success = false;
    },
    clearDeleteWatchListError: state => {
      state.deleteWatchList.error = null;
    },
    clearDeleteWatchListSuccess: state => {
      state.deleteWatchList.success = false;
    },
    clearExecutiveSponsorsError: state => {
      state.executiveSponsors.error = null;
    },
    clearCreateUsersBulkError: state => {
      state.createUsersBulk.error = null;
    },
    clearCreateUsersBulkSuccess: state => {
      state.createUsersBulk.success = false;
    },
    clearBulkUpload: state => {
      state.bulkUpload.error = null;
      state.bulkUpload.success = false;
    },
    clearDeleteAccountsBulkError: state => {
      state.deleteAccountsBulk.error = null;
    },
    clearDeleteAccountsBulkSuccess: state => {
      state.deleteAccountsBulk.success = false;
    },
    clearActivationReadyError: state => {
      state.activationReady.error = null;
    },
    clearAssignmentsOverviewError: state => {
      state.assignmentsOverview.error = null;
    },
    clearBulkActivationAction: state => {
      state.bulkActivationAction.error = null;
      state.bulkActivationAction.success = false;
    },
    clearSendPrepActivationEmail: state => {
      state.sendPrepActivationEmail.error = null;
      state.sendPrepActivationEmail.success = false;
    },
    clearCommandCenterMetricsError: state => {
      state.commandCenterMetrics.error = null;
    },
    clearCommandCenterAccountsNeedSponsorsError: state => {
      state.commandCenterAccountsNeedSponsors.error = null;
    },
    clearCommandCenterSponsorsOnBenchError: state => {
      state.commandCenterSponsorsOnBench.error = null;
    },
    clearCommandCenterAccountSurveysError: state => {
      state.commandCenterAccountSurveys.error = null;
    },
    // Add more ops-related reducers here as needed
  },
  extraReducers: builder => {
    builder
      // Fetch accounts with sponsors
      .addCase(fetchAccountsWithSponsors.pending, state => {
        state.accounts.loading = true;
        state.accounts.error = null;
      })
      .addCase(fetchAccountsWithSponsors.fulfilled, (state, action) => {
        state.accounts.loading = false;
        state.accounts.data = action.payload;
        state.accounts.error = null;
      })
      .addCase(fetchAccountsWithSponsors.rejected, (state, action) => {
        state.accounts.loading = false;
        state.accounts.error =
          (action.payload as string) || "Failed to fetch accounts";
      })
      // Create bulk accounts
      .addCase(createAccountsBulk.pending, state => {
        state.accounts.loading = true;
        state.accounts.error = null;
        state.accounts.success = false;
      })
      .addCase(createAccountsBulk.fulfilled, state => {
        state.accounts.loading = false;
        state.accounts.success = true;
        state.accounts.error = null;
        toast.success("Accounts created successfully!");
      })
      .addCase(createAccountsBulk.rejected, (state, action) => {
        state.accounts.loading = false;
        state.accounts.success = false;
        state.accounts.error =
          (action.payload as string) || "Failed to create accounts";
        toast.error((action.payload as string) || "Failed to create accounts");
      })
      // Delete bulk accounts
      .addCase(deleteAccountsBulk.pending, state => {
        state.deleteAccountsBulk.loading = true;
        state.deleteAccountsBulk.error = null;
        state.deleteAccountsBulk.success = false;
      })
      .addCase(deleteAccountsBulk.fulfilled, state => {
        state.deleteAccountsBulk.loading = false;
        state.deleteAccountsBulk.success = true;
        state.deleteAccountsBulk.error = null;
        toast.success("Accounts deletion request submitted successfully!");
      })
      .addCase(deleteAccountsBulk.rejected, (state, action) => {
        state.deleteAccountsBulk.loading = false;
        state.deleteAccountsBulk.success = false;
        state.deleteAccountsBulk.error =
          (action.payload as string) || "Failed to delete accounts";
        toast.error((action.payload as string) || "Failed to delete accounts");
      })
      // Fetch approval requests
      .addCase(fetchApprovalRequests.pending, state => {
        state.approvalRequests.loading = true;
        state.approvalRequests.error = null;
      })
      .addCase(fetchApprovalRequests.fulfilled, (state, action) => {
        state.approvalRequests.loading = false;
        state.approvalRequests.data = action.payload;
        state.approvalRequests.error = null;
      })
      .addCase(fetchApprovalRequests.rejected, (state, action) => {
        state.approvalRequests.loading = false;
        state.approvalRequests.error =
          (action.payload as string) || "Failed to fetch approval requests";
      })
      // Approve/Reject request
      .addCase(approveOrRejectRequest.pending, state => {
        state.approveReject.loading = true;
        state.approveReject.error = null;
        state.approveReject.success = false;
      })
      .addCase(approveOrRejectRequest.fulfilled, state => {
        state.approveReject.loading = false;
        state.approveReject.success = true;
        state.approveReject.error = null;
        // Toast is owned by OverviewContainer so approve/reject can show
        // action-specific copy ("Approved successfully" / "Rejected successfully").
      })
      .addCase(approveOrRejectRequest.rejected, (state, action) => {
        state.approveReject.loading = false;
        state.approveReject.success = false;
        state.approveReject.error =
          (action.payload as string) || "Failed to approve/reject request";
        // Error toast is owned by OverviewContainer (same reason as fulfilled).
      })
      // Fetch upload stats
      .addCase(fetchUploadStats.pending, state => {
        state.uploadStats.loading = true;
        state.uploadStats.error = null;
      })
      .addCase(fetchUploadStats.fulfilled, (state, action) => {
        state.uploadStats.loading = false;
        state.uploadStats.data = action.payload;
        state.uploadStats.error = null;
      })
      .addCase(fetchUploadStats.rejected, (state, action) => {
        state.uploadStats.loading = false;
        state.uploadStats.error =
          (action.payload as string) || "Failed to fetch upload stats";
      })
      // Fetch watch lists
      .addCase(fetchWatchLists.pending, state => {
        state.watchLists.loading = true;
        state.watchLists.error = null;
      })
      .addCase(fetchWatchLists.fulfilled, (state, action) => {
        state.watchLists.loading = false;
        state.watchLists.data = action.payload;
        state.watchLists.error = null;
      })
      .addCase(fetchWatchLists.rejected, (state, action) => {
        state.watchLists.loading = false;
        state.watchLists.error =
          (action.payload as string) || "Failed to fetch watch lists";
      })
      // Create watch list
      .addCase(createWatchList.pending, state => {
        state.createWatchList.loading = true;
        state.createWatchList.error = null;
        state.createWatchList.success = false;
      })
      .addCase(createWatchList.fulfilled, state => {
        state.createWatchList.loading = false;
        state.createWatchList.success = true;
        state.createWatchList.error = null;
        toast.success("Watch list created successfully!");
      })
      .addCase(createWatchList.rejected, (state, action) => {
        state.createWatchList.loading = false;
        state.createWatchList.success = false;
        state.createWatchList.error =
          (action.payload as string) || "Failed to create watch list";
        toast.error(
          (action.payload as string) || "Failed to create watch list"
        );
      })
      // Update watch list
      .addCase(updateWatchList.pending, state => {
        state.updateWatchList.loading = true;
        state.updateWatchList.error = null;
      })
      .addCase(updateWatchList.fulfilled, state => {
        state.updateWatchList.loading = false;
        state.updateWatchList.error = null;
        toast.success("Watch list updated successfully!");
      })
      .addCase(updateWatchList.rejected, (state, action) => {
        state.updateWatchList.loading = false;
        state.updateWatchList.error =
          (action.payload as string) || "Failed to update watch list";
        toast.error(
          (action.payload as string) || "Failed to update watch list"
        );
      })
      // Delete watch list
      .addCase(deleteWatchList.pending, state => {
        state.deleteWatchList.loading = true;
        state.deleteWatchList.error = null;
        state.deleteWatchList.success = false;
      })
      .addCase(deleteWatchList.fulfilled, (state, action) => {
        state.deleteWatchList.loading = false;
        state.deleteWatchList.success = true;
        state.deleteWatchList.error = null;
        if (state.watchLists.data) {
          state.watchLists.data = state.watchLists.data.filter(
            watchList => watchList.watch_list_id !== action.payload.watchListId
          );
        }
        toast.success("Group deleted successfully!");
      })
      .addCase(deleteWatchList.rejected, (state, action) => {
        state.deleteWatchList.loading = false;
        state.deleteWatchList.success = false;
        state.deleteWatchList.error =
          (action.payload as string) || "Failed to delete watch list";
        toast.error(
          (action.payload as string) || "Failed to delete watch list"
        );
      })
      // Fetch executive sponsors
      .addCase(fetchExecutiveSponsors.pending, state => {
        state.executiveSponsors.loading = true;
        state.executiveSponsors.error = null;
      })
      .addCase(fetchExecutiveSponsors.fulfilled, (state, action) => {
        state.executiveSponsors.loading = false;
        state.executiveSponsors.data = action.payload;
        state.executiveSponsors.error = null;
      })
      .addCase(fetchExecutiveSponsors.rejected, (state, action) => {
        state.executiveSponsors.loading = false;
        state.executiveSponsors.error =
          (action.payload as string) || "Failed to fetch executive sponsors";
      })
      // Create users bulk
      .addCase(createUsersBulk.pending, state => {
        state.createUsersBulk.loading = true;
        state.createUsersBulk.error = null;
        state.createUsersBulk.success = false;
      })
      .addCase(createUsersBulk.fulfilled, state => {
        state.createUsersBulk.loading = false;
        state.createUsersBulk.success = true;
        state.createUsersBulk.error = null;
        toast.success("Users created successfully!");
      })
      .addCase(createUsersBulk.rejected, (state, action) => {
        state.createUsersBulk.loading = false;
        state.createUsersBulk.success = false;
        state.createUsersBulk.error =
          (action.payload as string) || "Failed to create users";
        toast.error((action.payload as string) || "Failed to create users");
      })
      // Bulk upload
      .addCase(bulkUpload.pending, state => {
        state.bulkUpload.isUploading = true;
        state.bulkUpload.error = null;
        state.bulkUpload.success = false;
      })
      .addCase(bulkUpload.fulfilled, (state, action) => {
        state.bulkUpload.isUploading = false;
        state.bulkUpload.error = null;
        state.bulkUpload.success = true;
        toast.success(action.payload.message || "File uploaded successfully!");
      })
      .addCase(bulkUpload.rejected, (state, action) => {
        state.bulkUpload.isUploading = false;
        state.bulkUpload.error = action.payload as string;
        state.bulkUpload.success = false;
        toast.error((action.payload as string) || "Something went wrong");
      })
      .addCase(validateAccountTeamCsv.pending, state => {
        state.accountTeamCsvValidate.loading = true;
        state.accountTeamCsvValidate.error = null;
      })
      .addCase(validateAccountTeamCsv.fulfilled, (state, action) => {
        state.accountTeamCsvValidate.loading = false;
        state.accountTeamCsvValidate.data = action.payload ?? null;
        state.accountTeamCsvValidate.error = null;
      })
      .addCase(validateAccountTeamCsv.rejected, (state, action) => {
        state.accountTeamCsvValidate.loading = false;
        state.accountTeamCsvValidate.error =
          (action.payload as string) || "Failed to validate account team CSV";
      })
      // Fetch activation-ready data
      .addCase(fetchActivationReady.pending, state => {
        state.activationReady.loading = true;
        state.activationReady.error = null;
      })
      .addCase(fetchActivationReady.fulfilled, (state, action) => {
        state.activationReady.loading = false;
        state.activationReady.data = action.payload;
        state.activationReady.error = null;
      })
      .addCase(fetchActivationReady.rejected, (state, action) => {
        state.activationReady.loading = false;
        state.activationReady.error =
          (action.payload as string) || "Failed to fetch activation-ready data";
      })
      // Fetch assignments overview
      .addCase(fetchAssignmentsOverview.pending, state => {
        state.assignmentsOverview.loading = true;
        state.assignmentsOverview.error = null;
      })
      .addCase(fetchAssignmentsOverview.fulfilled, (state, action) => {
        state.assignmentsOverview.loading = false;
        state.assignmentsOverview.data = action.payload;
        state.assignmentsOverview.error = null;
      })
      .addCase(fetchAssignmentsOverview.rejected, (state, action) => {
        state.assignmentsOverview.loading = false;
        state.assignmentsOverview.error =
          (action.payload as string) || "Failed to fetch assignments overview";
      })
      // Bulk activation action
      .addCase(bulkActivationAction.pending, state => {
        state.bulkActivationAction.loading = true;
        state.bulkActivationAction.error = null;
        state.bulkActivationAction.success = false;
      })
      .addCase(bulkActivationAction.fulfilled, (state, action) => {
        state.bulkActivationAction.loading = false;
        state.bulkActivationAction.success = true;
        state.bulkActivationAction.error = null;
        toast.success("Bulk activation action completed successfully!");
      })
      .addCase(bulkActivationAction.rejected, (state, action) => {
        state.bulkActivationAction.loading = false;
        state.bulkActivationAction.error =
          (action.payload as string) ||
          "Failed to perform bulk activation action";
        state.bulkActivationAction.success = false;
        toast.error(
          (action.payload as string) ||
            "Failed to perform bulk activation action"
        );
      })
      // Send prep activation email
      .addCase(sendPrepActivationEmail.pending, state => {
        state.sendPrepActivationEmail.loading = true;
        state.sendPrepActivationEmail.error = null;
        state.sendPrepActivationEmail.success = false;
      })
      .addCase(sendPrepActivationEmail.fulfilled, (state, action) => {
        state.sendPrepActivationEmail.loading = false;
        state.sendPrepActivationEmail.success = true;
        state.sendPrepActivationEmail.error = null;
      })
      .addCase(sendPrepActivationEmail.rejected, (state, action) => {
        state.sendPrepActivationEmail.loading = false;
        state.sendPrepActivationEmail.error =
          (action.payload as string) || "Failed to send prep activation email";
        state.sendPrepActivationEmail.success = false;
      })
      // Fetch command center metrics
      .addCase(fetchCommandCenterMetrics.pending, state => {
        state.commandCenterMetrics.loading = true;
        state.commandCenterMetrics.error = null;
      })
      .addCase(fetchCommandCenterMetrics.fulfilled, (state, action) => {
        state.commandCenterMetrics.loading = false;
        state.commandCenterMetrics.data = action.payload;
        state.commandCenterMetrics.error = null;
      })
      .addCase(fetchCommandCenterMetrics.rejected, (state, action) => {
        state.commandCenterMetrics.loading = false;
        state.commandCenterMetrics.error =
          (action.payload as string) ||
          "Failed to fetch command center metrics";
      })
      // Fetch accounts need sponsors
      .addCase(fetchCommandCenterAccountsNeedSponsors.pending, state => {
        state.commandCenterAccountsNeedSponsors.loading = true;
        state.commandCenterAccountsNeedSponsors.error = null;
      })
      .addCase(
        fetchCommandCenterAccountsNeedSponsors.fulfilled,
        (state, action) => {
          state.commandCenterAccountsNeedSponsors.loading = false;
          state.commandCenterAccountsNeedSponsors.data = action.payload;
          state.commandCenterAccountsNeedSponsors.error = null;
        }
      )
      .addCase(
        fetchCommandCenterAccountsNeedSponsors.rejected,
        (state, action) => {
          state.commandCenterAccountsNeedSponsors.loading = false;
          state.commandCenterAccountsNeedSponsors.error =
            (action.payload as string) ||
            "Failed to fetch accounts need sponsors";
        }
      )
      // Fetch sponsors on bench
      .addCase(fetchCommandCenterSponsorsOnBench.pending, state => {
        state.commandCenterSponsorsOnBench.loading = true;
        state.commandCenterSponsorsOnBench.error = null;
      })
      .addCase(fetchCommandCenterSponsorsOnBench.fulfilled, (state, action) => {
        state.commandCenterSponsorsOnBench.loading = false;
        state.commandCenterSponsorsOnBench.data = action.payload;
        state.commandCenterSponsorsOnBench.error = null;
      })
      .addCase(fetchCommandCenterSponsorsOnBench.rejected, (state, action) => {
        state.commandCenterSponsorsOnBench.loading = false;
        state.commandCenterSponsorsOnBench.error =
          (action.payload as string) || "Failed to fetch sponsors on bench";
      })
      // Fetch account surveys
      .addCase(fetchCommandCenterAccountSurveys.pending, state => {
        state.commandCenterAccountSurveys.loading = true;
        state.commandCenterAccountSurveys.error = null;
      })
      .addCase(fetchCommandCenterAccountSurveys.fulfilled, (state, action) => {
        state.commandCenterAccountSurveys.loading = false;
        state.commandCenterAccountSurveys.data = action.payload;
        state.commandCenterAccountSurveys.error = null;
      })
      .addCase(fetchCommandCenterAccountSurveys.rejected, (state, action) => {
        state.commandCenterAccountSurveys.loading = false;
        state.commandCenterAccountSurveys.error =
          (action.payload as string) || "Failed to fetch account surveys";
      })
      .addCase(exportAccountsData.pending, state => {
        state.accountsExport.loading = true;
        state.accountsExport.error = null;
      })
      .addCase(exportAccountsData.fulfilled, state => {
        state.accountsExport.loading = false;
        state.accountsExport.error = null;
      })
      .addCase(exportAccountsData.rejected, (state, action) => {
        state.accountsExport.loading = false;
        state.accountsExport.error =
          (action.payload as string) || "Failed to export accounts";
        toast.error((action.payload as string) || "Failed to export accounts");
      });
  },
});

export const {
  clearAccountsError,
  clearAccountsSuccess,
  resetAccountsState,
  clearApprovalRequestsError,
  clearApproveRejectError,
  clearApproveRejectSuccess,
  clearUploadStatsError,
  clearWatchListsError,
  clearCreateWatchListError,
  clearCreateWatchListSuccess,
  clearDeleteWatchListError,
  clearDeleteWatchListSuccess,
  clearExecutiveSponsorsError,
  clearCreateUsersBulkError,
  clearCreateUsersBulkSuccess,
  clearBulkUpload,
  clearDeleteAccountsBulkError,
  clearDeleteAccountsBulkSuccess,
  clearActivationReadyError,
  clearAssignmentsOverviewError,
  clearBulkActivationAction,
  clearSendPrepActivationEmail,
  clearCommandCenterMetricsError,
  clearCommandCenterAccountsNeedSponsorsError,
  clearCommandCenterSponsorsOnBenchError,
  clearCommandCenterAccountSurveysError,
} = OpsSlice.actions;
export default OpsSlice.reducer;
